import {
  PHASE_ACTION_GUARD_FIELDS,
  checkIfTurnContextChanged,
  createPhaseActionGuard,
} from '../gameState/turn/phaseActionGuard';

/**
 * РАЗБОР ОТКАЗОВ УСТАРЕВШИХ ДЕЙСТВИЙ.
 *
 * Сервер отвергает действие, отправленное из уже закрытого хода
 * (shouldRejectStaleRelayedAction): отправитель получает STALE_ACTION_REJECTED и
 * полное состояние, а его клиент показывает оверлей и перерисовывает поле. Сам
 * отказ ничего не говорит о том, ЧТО закрыло ход и насколько действие опоздало,
 * — а от этого зависит, чем такие отказы лечить: отсрочкой серверного таймера
 * (TURN_AUTO_END_GRACE_MS), блокировкой ввода на клиенте или чем-то еще.
 *
 * Поэтому сервер ведет короткую историю закрытий хода в каждой партии
 * (createTurnClosureHistory) и к отказу прикладывает причину закрытия и
 * опоздание действия; клиент отправляет их событием аналитики
 * network:stale_action_rejected:<причина>:<опоздание>:<тип действия>
 * (getStaleActionRejectedEventValue).
 */

// Что закрыло ход
export const TURN_CLOSE_CAUSES = {
  // Серверный таймер хода/этапа (вместе с отсрочкой автозавершения)
  TIMER: 'timer',
  // Завершение, присланное клиентом: сам игрок, другой игрок или клиент,
  // исполняющий ход бота
  PLAYER: 'player',
  // Серверные авто-действия: этап, на котором ходящему нечего делать
  AUTO: 'auto',
  // Остальное на сервере: ходы ботов, доведение боя и расстановки, уход игрока
  SERVER: 'server',
  // Закрытие не нашлось в истории (или сервер старой версии его не прислал)
  UNKNOWN: 'unknown',
};

const KNOWN_CAUSES = new Set(Object.values(TURN_CLOSE_CAUSES));

// Сколько последних закрытий помнить. Действие опаздывает на доли секунды, а
// за это время ход меняется раз-другой; запас — на цепочки авто-переходов
const TURN_CLOSURE_HISTORY_LIMIT = 32;

const checkIfGuardMatchesPayload = (guard, payload) => PHASE_ACTION_GUARD_FIELDS
  .every((field) => payload[field] === undefined || payload[field] === guard[field]);

/**
 * История закрытий хода одной партии.
 *
 *  - track(gameState, cause, atMs) — зовется на каждое изменение состояния:
 *    если контекст хода (раунд, фаза, этап, ходящий) сменился, прежний
 *    запоминается закрытым в atMs по причине cause;
 *  - find(action, nowMs) — чем и как давно закрыт ход, из которого отправлено
 *    действие (по слепку хода в его payload): {closedBy, lateMs}. Не нашлось —
 *    {closedBy: 'unknown', lateMs: null}.
 */
export const createTurnClosureHistory = (initialGameState, limit = TURN_CLOSURE_HISTORY_LIMIT) => {
  const closures = [];
  let currentGuard = createPhaseActionGuard(initialGameState);

  return {
    track: (gameState, cause, atMs) => {
      const guard = createPhaseActionGuard(gameState);
      if (!checkIfTurnContextChanged(currentGuard, guard)) {
        return;
      }
      closures.push({guard: currentGuard, closedBy: cause, atMs});
      if (closures.length > limit) {
        closures.shift();
      }
      currentGuard = guard;
    },
    find: (action, nowMs) => {
      const payload = action?.payload;
      const hasGuard = payload && typeof payload === 'object'
        && PHASE_ACTION_GUARD_FIELDS.some((field) => payload[field] !== undefined);
      if (hasGuard) {
        // С конца: тот же контекст хода мог повториться, нужен последний
        for (let index = closures.length - 1; index >= 0; index -= 1) {
          if (checkIfGuardMatchesPayload(closures[index].guard, payload)) {
            return {closedBy: closures[index].closedBy, lateMs: Math.max(0, nowMs - closures[index].atMs)};
          }
        }
      }
      return {closedBy: TURN_CLOSE_CAUSES.UNKNOWN, lateMs: null};
    },
  };
};

// Опоздание действия — ступенями: по ним видно, какой отсрочки хватило бы.
// Имена — в пределах символов, допустимых в событии GameAnalytics
const LATENESS_BUCKETS = [
  [100, 'under_100ms'],
  [250, '100_250ms'],
  [500, '250_500ms'],
  [1000, '500_1000ms'],
  [2000, '1_2s'],
  [5000, '2_5s'],
];
const LATENESS_BUCKET_OVER = 'over_5s';
const LATENESS_BUCKET_UNKNOWN = 'unknown';

export const getStaleActionLatenessBucket = (lateMs) => {
  if (!Number.isFinite(lateMs) || lateMs < 0) {
    return LATENESS_BUCKET_UNKNOWN;
  }
  const bucket = LATENESS_BUCKETS.find(([limitMs]) => lateMs < limitMs);
  return bucket ? bucket[1] : LATENESS_BUCKET_OVER;
};

const ACTION_TYPE_REGEX = /^[A-Za-z0-9_]{1,64}$/;

/**
 * Значение события аналитики для отказа: «причина:опоздание:тип действия».
 * Вместе с network:stale_action_rejected это ровно пять уровней — потолок
 * события GameAnalytics. Все, чего в сообщении сервера нет или что не похоже на
 * ожидаемое, заменяется на unknown: событие с посторонним символом SDK молча
 * выбросил бы целиком.
 *
 * @param {Object} [payload] - payload сообщения STALE_ACTION_REJECTED
 * @returns {string}
 */
export const getStaleActionRejectedEventValue = (payload) => {
  const closedBy = KNOWN_CAUSES.has(payload?.closedBy) ? payload.closedBy : TURN_CLOSE_CAUSES.UNKNOWN;
  const actionType = ACTION_TYPE_REGEX.test(payload?.actionType || '') ? payload.actionType : 'unknown';
  return `${closedBy}:${getStaleActionLatenessBucket(payload?.lateMs)}:${actionType}`;
};
